import React, { useState } from 'react';
import {
    Alert,
    Button,
    Card,
    Col,
    Input,
    Row,
    Select,
    Space,
    Tag,
    Tooltip,
    Typography,
    message,
} from 'antd';
import { BulbOutlined, PlayCircleOutlined, PlusOutlined } from '@ant-design/icons';
import { useAuth } from '../../../hooks/useAuth';
import {
    aiAdminApi,
    ModelBeliefsAdhocTarget,
    ModelBeliefsCategorySuggestions,
    ModelBeliefsCompetitorSuggestions,
    ModelBeliefsPricePositioning,
} from '../../../services/ai-admin-api';
import { COUNTRY_OPTIONS } from '../../../constants/countries';
import { TEMPLATE_TYPES } from './templateTypes';
import { ModelBeliefsRunModal, ModelBeliefsRunTarget } from './ModelBeliefsRunModal';

const { Text } = Typography;

// "2/3": named by two of the three suggestion runs. Nothing on a single run.
const votesTag = (votes: number | undefined, runs: number | undefined) =>
    runs && runs > 1 && votes ? (
        <Tooltip title={`Named by ${votes} of ${runs} suggestion runs`}>
            <Tag>{votes}/{runs}</Tag>
        </Tooltip>
    ) : null;
const votesText = (votes: number | undefined, runs: number | undefined) =>
    runs && runs > 1 && votes ? (
        <Text type="secondary" style={{ fontSize: 11, marginLeft: 6 }}>
            {votes}/{runs}
        </Text>
    ) : null;
const { TextArea } = Input;

interface FormState {
    name: string;
    consumerName: string;
    websiteUrl: string;
    templateType: string;
    country: string;
    city: string;
    category: string;
    competitorNames: string[];
    disambiguation: string;
    pricePositioning: ModelBeliefsPricePositioning | '';
}

const EMPTY: FormState = {
    name: '',
    consumerName: '',
    websiteUrl: '',
    templateType: 'durable',
    country: 'AU',
    city: '',
    category: '',
    competitorNames: [],
    disambiguation: '',
    pricePositioning: '',
};

const tagOptions = (values: string[]) => values.map((v) => ({ value: v, label: v }));

interface Props {
    onStarted: (runId: number) => void;
}

// A read of a brand that is not on the platform. The same inputs a brand
// config holds, entered once: the site gives the category suggestions and the
// competitor suggestions, a person picks, and the run is estimated before it
// is started. Attached to the brand later if it signs up.
export const ModelBeliefsAdhocForm: React.FC<Props> = ({ onStarted }) => {
    const { user } = useAuth();
    const jwt = user?.jwtToken ?? '';

    const [form, setForm] = useState<FormState>(EMPTY);
    const [categories, setCategories] = useState<ModelBeliefsCategorySuggestions | null>(null);
    const [suggestingCategories, setSuggestingCategories] = useState(false);
    const [competitors, setCompetitors] = useState<ModelBeliefsCompetitorSuggestions | null>(null);
    const [suggestingCompetitors, setSuggestingCompetitors] = useState(false);
    const [runTarget, setRunTarget] = useState<ModelBeliefsRunTarget | null>(null);

    const set = <K extends keyof FormState>(key: K, value: FormState[K]) =>
        setForm((prev) => ({ ...prev, [key]: value }));

    const siteReady = form.name.trim().length > 0 && form.websiteUrl.trim().length > 0;
    const categoryReady = siteReady && form.category.trim().length > 0;
    const ready = categoryReady && form.country.trim().length === 2 && form.templateType.trim().length > 0;

    const suggestCategories = async () => {
        if (!jwt) return;
        setSuggestingCategories(true);
        try {
            const result = await aiAdminApi.suggestModelBeliefsAdhocCategories(jwt, {
                websiteUrl: form.websiteUrl.trim(),
                name: (form.consumerName || form.name).trim(),
                country: form.country,
            });
            setCategories(result);
            // A brand not on the platform has nobody to set this later, so
            // the site's answer pre-fills the select; the person can change it.
            const side = result.pricePositioning?.positioning;
            if (!form.pricePositioning && (side === 'premium' || side === 'value')) {
                set('pricePositioning', side);
            }
        } catch (e) {
            message.error((e as Error).message);
        } finally {
            setSuggestingCategories(false);
        }
    };

    const suggestCompetitors = async () => {
        if (!jwt) return;
        setSuggestingCompetitors(true);
        try {
            setCompetitors(
                await aiAdminApi.suggestModelBeliefsAdhocCompetitors(jwt, {
                    websiteUrl: form.websiteUrl.trim(),
                    name: (form.consumerName || form.name).trim(),
                    country: form.country,
                    category: form.category.trim(),
                }),
            );
        } catch (e) {
            message.error((e as Error).message);
        } finally {
            setSuggestingCompetitors(false);
        }
    };

    const addCompetitor = (name: string) =>
        setForm((prev) =>
            prev.competitorNames.some((n) => n.trim().toLowerCase() === name.toLowerCase())
                ? prev
                : { ...prev, competitorNames: [...prev.competitorNames, name] },
        );

    const target = (): ModelBeliefsAdhocTarget => ({
        name: form.name.trim(),
        consumerName: form.consumerName.trim() || undefined,
        websiteUrl: form.websiteUrl.trim(),
        category: form.category.trim(),
        country: form.country,
        city: form.city.trim() || undefined,
        competitorNames: form.competitorNames.map((n) => n.trim()).filter(Boolean),
        disambiguation: form.disambiguation.trim() || undefined,
        pricePositioning: form.pricePositioning || null,
    });

    const unusedCompetitors = (competitors?.suggestions ?? []).filter(
        (c) => !form.competitorNames.some((n) => n.trim().toLowerCase() === c.name.toLowerCase()),
    );

    return (
        <Space direction="vertical" size={16} style={{ width: '100%' }}>
            <Alert
                type="info"
                showIcon
                message="A read of a brand that is not on the platform"
                description="Starting the read saves this form as a target under Not on the platform in the Brands tab, where it can be edited and read again. The read is booked as platform spend and shows only in the Hub. If the brand signs up, attach the run to it from the Runs tab and it becomes the brand's first read."
            />

            <Card size="small" title="The brand">
                <Row gutter={16}>
                    <Col span={12}>
                        <Space direction="vertical" size={4} style={{ width: '100%' }}>
                            <Text strong style={{ fontSize: 13 }}>
                                Brand name
                            </Text>
                            <Input
                                value={form.name}
                                placeholder="Bed Threads"
                                status={form.name.trim() ? undefined : 'error'}
                                onChange={(e) => set('name', e.target.value)}
                            />
                        </Space>
                    </Col>
                    <Col span={12}>
                        <Space direction="vertical" size={4} style={{ width: '100%' }}>
                            <Text strong style={{ fontSize: 13 }}>
                                Name shoppers use
                            </Text>
                            <Input
                                value={form.consumerName}
                                placeholder={form.name || 'same as the brand name'}
                                onChange={(e) => set('consumerName', e.target.value)}
                            />
                            <Text type="secondary" style={{ fontSize: 11 }}>
                                Only when it differs: nobody bets with Tabcorp, they bet with TAB.
                            </Text>
                        </Space>
                    </Col>
                </Row>
                <Row gutter={16} style={{ marginTop: 12 }}>
                    <Col span={12}>
                        <Space direction="vertical" size={4} style={{ width: '100%' }}>
                            <Text strong style={{ fontSize: 13 }}>
                                Website
                            </Text>
                            <Input
                                value={form.websiteUrl}
                                placeholder="bedthreads.com.au"
                                status={form.websiteUrl.trim() ? undefined : 'error'}
                                onChange={(e) => set('websiteUrl', e.target.value)}
                            />
                            <Text type="secondary" style={{ fontSize: 11 }}>
                                Read for the suggestions below, and counted as the brand&rsquo;s own site
                                among the pages the models read.
                            </Text>
                        </Space>
                    </Col>
                    <Col span={6}>
                        <Space direction="vertical" size={4} style={{ width: '100%' }}>
                            <Text strong style={{ fontSize: 13 }}>
                                Question template
                            </Text>
                            <Select
                                style={{ width: '100%' }}
                                value={form.templateType}
                                onChange={(value: string) => set('templateType', value)}
                                options={TEMPLATE_TYPES.map((t) => ({ value: t, label: t }))}
                            />
                        </Space>
                    </Col>
                    <Col span={6}>
                        <Space direction="vertical" size={4} style={{ width: '100%' }}>
                            <Text strong style={{ fontSize: 13 }}>
                                Market
                            </Text>
                            <Select
                                showSearch
                                style={{ width: '100%' }}
                                value={form.country}
                                onChange={(value: string) => set('country', value)}
                                optionFilterProp="label"
                                options={COUNTRY_OPTIONS.map((c) => ({
                                    value: c.code,
                                    label: `${c.flag} ${c.name}`,
                                }))}
                            />
                        </Space>
                    </Col>
                </Row>
                <Row gutter={16} style={{ marginTop: 12 }}>
                    <Col span={12}>
                        <Space direction="vertical" size={4} style={{ width: '100%' }}>
                            <Text strong style={{ fontSize: 13 }}>
                                City (optional)
                            </Text>
                            <Input
                                value={form.city}
                                placeholder="Sydney"
                                onChange={(e) => set('city', e.target.value)}
                            />
                        </Space>
                    </Col>
                    <Col span={12}>
                        <Space direction="vertical" size={4} style={{ width: '100%' }}>
                            <Text strong style={{ fontSize: 13 }}>
                                Disambiguation (optional)
                            </Text>
                            <TextArea
                                rows={2}
                                value={form.disambiguation}
                                placeholder="the Australian bed linen brand, not the US sewing-thread company"
                                onChange={(e) => set('disambiguation', e.target.value)}
                            />
                        </Space>
                    </Col>
                    <Col span={12}>
                        <Space direction="vertical" size={4} style={{ width: '100%' }}>
                            <Text strong style={{ fontSize: 13 }}>
                                Price positioning (optional)
                            </Text>
                            <Select
                                style={{ width: '100%' }}
                                value={form.pricePositioning}
                                onChange={(value) => set('pricePositioning', value)}
                                options={[
                                    { value: '', label: 'Not set: no record fix on price' },
                                    { value: 'premium', label: 'Premium: a high price is the brand\'s side' },
                                    { value: 'value', label: 'Value: a low price is the brand\'s side' },
                                ]}
                            />
                            {categories?.pricePositioning && (
                                <Text type="secondary" style={{ fontSize: 11 }}>
                                    From the site: <strong>{categories.pricePositioning.positioning}</strong>
                                    {categories.pricePositioning.runs && categories.pricePositioning.runs > 1
                                        ? ` (${categories.pricePositioning.votes}/${categories.pricePositioning.runs})`
                                        : ''}
                                    {categories.pricePositioning.evidence
                                        ? ` \u201c${categories.pricePositioning.evidence}\u201d`
                                        : ''}
                                </Text>
                            )}
                        </Space>
                    </Col>
                </Row>
            </Card>

            <Card size="small" title="Category">
                <Space direction="vertical" size={6} style={{ width: '100%' }}>
                    <Input
                        value={form.category}
                        placeholder="bed linen"
                        status={form.category.trim() ? undefined : 'error'}
                        onChange={(e) => set('category', e.target.value)}
                    />
                    <Text type="secondary" style={{ fontSize: 11 }}>
                        A shopper&rsquo;s words for what they are buying, never the brand name. This is
                        what the rank question asks about, so the read cannot start without it.
                    </Text>
                    <Space size={8} wrap>
                        <Tooltip title="Reads the homepage and asks the suggestion model (Hub: Configure), with web search, for up to five categories a shopper searches for separately. One call, a few cents.">
                            <Button
                                size="small"
                                icon={<BulbOutlined style={{ marginRight: 4 }} />}
                                loading={suggestingCategories}
                                disabled={!siteReady}
                                onClick={() => void suggestCategories()}
                            >
                                {categories ? 'Suggest again' : 'Suggest from website'}
                            </Button>
                        </Tooltip>
                        {categories && (
                            <Text type="secondary" style={{ fontSize: 11 }}>
                                From {categories.site}
                                {categories.runs && categories.runs > 1 ? `, ${categories.runs} runs merged` : ''}
                            </Text>
                        )}
                    </Space>
                    {categories && categories.suggestions.length > 0 && (
                        <div
                            style={{
                                display: 'grid',
                                gridTemplateColumns: 'minmax(150px, max-content) max-content 1fr max-content',
                                alignItems: 'center',
                                columnGap: 14,
                                rowGap: 8,
                                padding: '10px 12px',
                                background: '#fafafa',
                                borderRadius: 6,
                            }}
                        >
                            {categories.suggestions.map((c) => {
                                const chosen = form.category.trim().toLowerCase() === c.phrase;
                                return (
                                    <React.Fragment key={c.phrase}>
                                        <Text strong style={{ fontSize: 13 }}>{c.phrase}</Text>
                                        <Space size={4}>
                                            {c.main && <Tag color="gold" style={{ marginRight: 0 }}>main</Tag>}
                                            {votesTag(c.votes, categories.runs)}
                                        </Space>
                                        <Text type="secondary" style={{ fontSize: 12, minWidth: 0 }}>
                                            {c.covers}
                                        </Text>
                                        <Button
                                            size="small"
                                            type={chosen ? 'default' : 'link'}
                                            disabled={chosen}
                                            onClick={() => set('category', c.phrase)}
                                        >
                                            {chosen ? 'In use' : 'Use this'}
                                        </Button>
                                    </React.Fragment>
                                );
                            })}
                        </div>
                    )}
                </Space>
            </Card>

            <Card size="small" title="Competitors">
                <Space direction="vertical" size={8} style={{ width: '100%' }}>
                    <Select
                        mode="tags"
                        style={{ width: '100%' }}
                        value={form.competitorNames}
                        options={tagOptions(form.competitorNames)}
                        onChange={(values: string[]) => set('competitorNames', values)}
                        tokenSeparators={[',', '\n']}
                        placeholder="Type a name, or suggest from the site"
                    />
                    <Text type="secondary" style={{ fontSize: 11 }}>
                        Read alongside the brand, first ones first up to the Hub&rsquo;s competitors-per-read
                        limit. The rank question reports every brand the models list either way.
                    </Text>
                    <Space size={8} wrap>
                        <Tooltip
                            title={
                                categoryReady
                                    ? 'Asks the suggestion model (Hub: Configure), with web search, which brands a shopper would weigh this one against when buying the category. One call, a few cents.'
                                    : 'Set the category first: competitors are the brands that sell the same kind of product.'
                            }
                        >
                            <Button
                                size="small"
                                icon={<BulbOutlined style={{ marginRight: 4 }} />}
                                loading={suggestingCompetitors}
                                disabled={!categoryReady}
                                onClick={() => void suggestCompetitors()}
                            >
                                {competitors ? 'Suggest again' : 'Suggest competitors'}
                            </Button>
                        </Tooltip>
                        {competitors && (
                            <Text type="secondary" style={{ fontSize: 11 }}>
                                For {competitors.category}, from {competitors.site}
                                {competitors.runs && competitors.runs > 1 ? `, ${competitors.runs} runs merged` : ''}
                            </Text>
                        )}
                    </Space>
                    {unusedCompetitors.length > 0 && (
                        <Space size={4} wrap>
                            {unusedCompetitors.map((c) => (
                                <Tooltip key={c.name} title={c.why || undefined}>
                                    <Button
                                        size="small"
                                        icon={<PlusOutlined style={{ marginRight: 4 }} />}
                                        onClick={() => addCompetitor(c.name)}
                                    >
                                        {c.name}
                                        {votesText(c.votes, competitors?.runs)}
                                    </Button>
                                </Tooltip>
                            ))}
                        </Space>
                    )}
                </Space>
            </Card>

            <Space>
                <Button
                    type="primary"
                    icon={<PlayCircleOutlined style={{ marginRight: 4 }} />}
                    disabled={!ready}
                    onClick={() =>
                        setRunTarget({ kind: 'adhoc', target: target(), templateType: form.templateType })
                    }
                >
                    Estimate and run
                </Button>
                {!ready && (
                    <Text type="secondary" style={{ fontSize: 12 }}>
                        Needs the brand name, website, category, market and template.
                    </Text>
                )}
            </Space>

            <ModelBeliefsRunModal
                open={!!runTarget}
                target={runTarget}
                onClose={() => setRunTarget(null)}
                onStarted={onStarted}
            />
        </Space>
    );
};
